import { HD_BODYGRAPH_COLORS } from '../bodygraph/hd-bodygraph-metadata';
import type { HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import { getHdPlanetDisplayConfigByPlanetId } from '../bodygraph/planet-grid/hd-planet-display-config';
import { HD_QUARTER_STARTS, HD_SEASON_MARKERS, hdSeasonOfQuarter } from './hd-season-markers';
import type { HdMandalaGateGlyph, HdMandalaQuarterTexts, HdMandalaSeasonTexts, HdMandalaZodiacNames } from './mandala-wheel.component';
import { MANDALA_QUARTERS, MANDALA_ZODIAC_SIGNS } from './mandala.constants';

/**
 * Invoer voor `hd-mandala-wheel` die de chartpagina en de PDF-print op dezelfde manier opbouwen
 * (glyphs in de activatie-zone, kwartaal- en tekennamen in de contenttaal).
 */

/** Standaardkleur van een glyph: design-rood of personality-zwart, zoals in de bodygraph. */
export function hdMandalaSideGlyphColor(activation: HdBodygraphPlanetaryActivation): string {
  return activation.side === 'design' ? HD_BODYGRAPH_COLORS.design : HD_BODYGRAPH_COLORS.personality;
}

/**
 * Planeetglyphs voor de activatie-zone van het wiel. Enkel de chart-zijden (design, personality);
 * design vóór personality en dan de planeetvolgorde, want de volgorde bepaalt de stapelvolgorde
 * in de slice. `prefix` houdt de keys uniek wanneer meerdere bronnen samen in het wiel staan.
 */
export function buildHdMandalaGateGlyphs(
  activations: readonly HdBodygraphPlanetaryActivation[],
  prefix: string,
  colorFor: (activation: HdBodygraphPlanetaryActivation) => string,
): HdMandalaGateGlyph[] {
  const sideRank = (activation: HdBodygraphPlanetaryActivation): number => (activation.side === 'design' ? 0 : 1);
  return activations
    .filter(activation => activation.side === 'design' || activation.side === 'personality')
    .sort((a, b) => sideRank(a) - sideRank(b) || a.planetId - b.planetId)
    .flatMap(activation => {
      const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
      if (config == null) {
        return [];
      }
      return [{
        key: `${prefix}-${activation.id}`,
        gate: activation.gate,
        glyph: config.astronomiconChar,
        color: colorFor(activation),
      }];
    });
}

/**
 * Kwartaal-keynote-delen (i18n-keys `humanDesign.chart.mandala.quarters.*`). `translate` vertaalt
 * een volledige key in de gewenste taal.
 */
export function buildHdMandalaQuarterTexts(translate: (key: string) => string): HdMandalaQuarterTexts {
  const texts: Record<string, { title: string; purpose: string }> = {};
  for (const quarter of MANDALA_QUARTERS) {
    texts[quarter.key] = {
      title: translate(`humanDesign.chart.mandala.quarters.${quarter.key}.title`),
      purpose: translate(`humanDesign.chart.mandala.quarters.${quarter.key}.purpose`),
    };
  }
  return texts;
}

/** Dierenriem-tekennamen (i18n-keys `humanDesign.chart.mandala.zodiac.*`). */
export function buildHdMandalaZodiacNames(translate: (key: string) => string): HdMandalaZodiacNames {
  const names: Record<string, string> = {};
  for (const sign of MANDALA_ZODIAC_SIGNS) {
    names[sign.key] = translate(`humanDesign.chart.mandala.zodiac.${sign.key}`);
  }
  return names;
}

/**
 * Tooltips van de seizoensmarkeringen en kwartaalbanden (i18n-keys
 * `humanDesign.chart.mandala.{seasons,seasonTooltip,quarterTooltip,quarterStarts}`).
 * `translate` vertaalt een volledige key met parameters in de gewenste taal.
 */
export function buildHdMandalaSeasonTexts(
  translate: (key: string, params?: Record<string, unknown>) => string,
): HdMandalaSeasonTexts {
  const prefix = 'humanDesign.chart.mandala';
  const seasonName = (key: string) => translate(`${prefix}.seasons.${key}`);
  const seasons: Record<string, string> = {};
  for (const marker of HD_SEASON_MARKERS) {
    seasons[marker.key] = translate(`${prefix}.seasonTooltip`, {
      season: seasonName(marker.key),
      sign: translate(`${prefix}.zodiac.${marker.zodiacKey}`),
      sunGate: marker.sunGate,
      earthGate: marker.earthGate,
    });
  }
  const quarters: Record<string, string> = {};
  for (const quarter of HD_QUARTER_STARTS) {
    const season = hdSeasonOfQuarter(quarter);
    quarters[quarter.key] = translate(`${prefix}.quarterTooltip`, {
      title: translate(`${prefix}.quarters.${quarter.key}.title`),
      startGate: quarter.gate,
      endGate: quarter.endGate,
      around: translate(`${prefix}.quarterStarts.${quarter.key}`),
      midGate: quarter.midGate,
      season: season != null ? seasonName(season.key) : '',
      lines: quarter.bottomLines,
    });
  }
  return { seasons, quarters };
}
